import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Copy, Image as ImageIcon, RefreshCw, X } from "lucide-react";
import { api } from "@/lib/api";

export default function AdminMedia() {
    const [uploads, setUploads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [previewing, setPreviewing] = useState(null);
    const fileInput = useRef(null);

    const fetchUploads = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/admin/cms/uploads");
            setUploads(res.data || []);
        } catch (e) {
            toast.error("Falha ao carregar a galeria");
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchUploads(); }, [fetchUploads]);

    async function handleUpload(files) {
        if (!files || files.length === 0) return;
        setUploading(true);
        let success = 0;
        for (const file of files) {
            const fd = new FormData();
            fd.append("file", file);
            try {
                await api.post("/admin/cms/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                success++;
            } catch (err) {
                toast.error(`Falha em ${file.name}: ${err?.response?.data?.detail || ""}`);
            }
        }
        if (success > 0) toast.success(`${success} imagem${success > 1 ? "s" : ""} carregada${success > 1 ? "s" : ""}`);
        setUploading(false);
        await fetchUploads();
    }

    function copyUrl(url) {
        const full = url.startsWith("http") ? url : `${window.location.origin}${url}`;
        navigator.clipboard.writeText(full);
        toast.success("URL copiado");
    }

    async function remove(filename) {
        if (!window.confirm(`Eliminar ${filename}?`)) return;
        try {
            await api.delete(`/admin/cms/upload/${encodeURIComponent(filename)}`);
            toast.success("Eliminado");
            await fetchUploads();
        } catch (err) {
            toast.error("Falha ao eliminar");
        }
    }

    return (
        <div className="space-y-6" data-testid="admin-media-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-heading text-2xl lg:text-3xl font-extrabold">Galeria de imagens</h2>
                    <p className="text-sm text-muted-foreground mt-1">Gerir uploads. Use estes URLs em qualquer campo de imagem do CMS.</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={fetchUploads} className="inline-flex items-center gap-2 rounded-md border border-border bg-white px-3 py-2 text-sm font-bold text-primary hover:bg-muted">
                        <RefreshCw className="h-3.5 w-3.5" /> Recarregar
                    </button>
                    <button onClick={() => fileInput.current?.click()} disabled={uploading} data-testid="media-upload-btn"
                        className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-bold text-white hover:bg-primary/90 disabled:opacity-50">
                        <Upload className="h-3.5 w-3.5" /> {uploading ? "A enviar..." : "Carregar imagens"}
                    </button>
                    <input ref={fileInput} type="file" accept="image/*" hidden multiple
                        onChange={(e) => handleUpload(Array.from(e.target.files || []))} />
                </div>
            </div>

            {/* Drop zone */}
            <div
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-primary", "bg-primary/5"); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove("border-primary", "bg-primary/5"); }}
                onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove("border-primary", "bg-primary/5");
                    handleUpload(Array.from(e.dataTransfer.files || []));
                }}
                className="rounded-xl border-2 border-dashed border-border bg-white p-8 text-center transition cursor-pointer hover:bg-muted/40"
                onClick={() => fileInput.current?.click()}
                data-testid="media-dropzone"
            >
                <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm font-semibold text-primary">Arraste imagens aqui ou clique para selecionar</p>
                <p className="text-xs text-muted-foreground mt-1">JPG, PNG, WebP, SVG, GIF — até 8 MB cada</p>
            </div>

            {/* Gallery */}
            {loading ? (
                <p className="text-sm text-muted-foreground">A carregar...</p>
            ) : uploads.length === 0 ? (
                <div className="rounded-xl border border-border bg-white p-12 text-center text-sm text-muted-foreground">
                    <ImageIcon className="h-10 w-10 mx-auto mb-3 opacity-50" />
                    Sem imagens. Faça o seu primeiro upload acima.
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="media-grid">
                    {uploads.map((u) => (
                        <div key={u.filename} className="rounded-lg border border-border bg-white overflow-hidden group" data-testid={`media-item-${u.filename}`}>
                            <button onClick={() => setPreviewing(u)} className="block w-full">
                                <img src={u.url} alt={u.original_name} className="h-32 w-full object-cover" />
                            </button>
                            <div className="p-2.5 text-[11px]">
                                <div className="font-mono truncate" title={u.filename}>{u.filename}</div>
                                <div className="text-muted-foreground text-[10px] mt-0.5">
                                    {u.size ? `${(u.size / 1024).toFixed(0)} KB` : ""}
                                </div>
                            </div>
                            <div className="px-2.5 pb-2.5 flex gap-1.5">
                                <button onClick={() => copyUrl(u.url)} title="Copiar URL"
                                    className="flex-1 inline-flex items-center justify-center gap-1 rounded-md border border-border bg-white px-2 py-1.5 text-[11px] font-semibold text-primary hover:bg-muted">
                                    <Copy className="h-3 w-3" /> URL
                                </button>
                                <button onClick={() => remove(u.filename)} title="Eliminar"
                                    className="inline-flex items-center justify-center rounded-md border border-red-200 bg-white px-2 py-1.5 text-red-600 hover:bg-red-50">
                                    <Trash2 className="h-3 w-3" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {previewing && (
                <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setPreviewing(null)}>
                    <div className="bg-white rounded-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
                            <div>
                                <div className="font-semibold text-sm">{previewing.original_name}</div>
                                <div className="text-xs text-muted-foreground">{previewing.filename}</div>
                            </div>
                            <button onClick={() => setPreviewing(null)} className="text-muted-foreground hover:text-primary">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="p-5">
                            <img src={previewing.url} alt={previewing.original_name} className="w-full max-h-[60vh] object-contain rounded" />
                            <div className="mt-4 flex items-center gap-2">
                                <input readOnly value={`${window.location.origin}${previewing.url}`} className="flex-1 rounded-md border border-border bg-muted px-3 py-2 text-xs font-mono" />
                                <button onClick={() => copyUrl(previewing.url)} className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-xs font-bold text-white">
                                    <Copy className="h-3 w-3" /> Copiar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
